// apps/client/src/ui/screens/gift.tsx (M2-T11) — pick a pocket item to give the villager you are talking to (spec §9.13). A tap picks it
// and names it; Give hands it over in the open conversation (`villager.gift`) and goes back to the talk, where the villager's reaction
// plays; closing without a gift goes back to the talk too. Tools are not offered (M2 playtest: one tap gave away the only fishing rod).
import { getEntry } from '@whiskerton/content';
import type { ItemId, ItemView, VillagerId } from '@whiskerton/shared';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useGame } from '../../state/store';
import { itemName, useT } from '../i18n';
import { useIcon } from '../items/useIcon';
import { Panel } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { gift: { villager: VillagerId } } }

const nameOf = (item: ItemView) => { const e = getEntry(item.catalogId); return e && e.kind !== 'world' ? itemName(e.name) : item.catalogId; };
const giftable = (item: ItemView) => item.at.loc === 'pockets' && getEntry(item.catalogId)?.kind !== 'tool';

function GiftSlot({ item, selected, onPick }: { item: ItemView; selected: boolean; onPick(): void }) {
  const icon = useIcon(item.catalogId);
  const name = nameOf(item);
  return (
    <button type="button" className={`slot${selected ? ' slot-selected' : ''}`} data-testid="gift-slot" aria-label={name} aria-pressed={selected} onClick={onPick}>
      <img src={icon} alt="" />
      {item.qty > 1 && <span className="slot-qty">{item.qty}</span>}
    </button>
  );
}

function Gift({ villager, close }: { villager: VillagerId; close(): void }) {
  const t = useT();
  const name = useGame((s) => s.villagersList.find((x) => x.id === villager)?.name ?? '');
  const items = useGame(useShallow((s) => (s.you?.items ?? []).filter(giftable)
    .sort((a, b) => (a.at.loc === 'pockets' && b.at.loc === 'pockets' ? a.at.slot - b.at.slot : 0))));
  const done = useRef(false), busy = useRef(false);
  const [picked, setPicked] = useState<ItemId | null>(null);
  const pickedItem = items.find((i) => i.id === picked) ?? null;
  const back = () => { if (useGame.getState().villagersTalk?.villager === villager) useGame.getState().openScreen('villagerTalk', { villager }); };
  // Closed without a gift (the scrim, B): back to the conversation.
  useEffect(() => () => { if (!done.current) { done.current = true; back(); } }, []);   // on unmount only
  const give = async (id: ItemId) => {
    if (busy.current) return;
    busy.current = true;
    await useGame.getState().villagersGift(id);
    done.current = true;
    close();
    back();
  };
  return (
    <Panel title={t('villagers.giftTitle', { name })}>
      <div style={{ display: 'grid', gap: 14, width: 'min(60cqw, 560px)', maxWidth: '100%' }}>
        {items.length === 0
          ? <p style={{ margin: 0, fontWeight: 800 }}>{t('villagers.giftNone')}</p>
          : <div className="slot-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1.1cqw' }}>
              {items.map((i) => <GiftSlot key={i.id} item={i} selected={i.id === picked} onPick={() => setPicked(i.id)} />)}
            </div>}
        {items.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span data-testid="gift-picked" style={{ fontWeight: 900 }}>{pickedItem ? nameOf(pickedItem) : t('villagers.giftPick')}</span>
            <button type="button" className="btn btn-primary" data-testid="gift-give" disabled={!pickedItem}
              onClick={() => { if (pickedItem) void give(pickedItem.id); }}>{t('villagers.giftGive')}</button>
          </div>
        )}
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'gift'> = { id: 'gift', layer: 'panel', component: ({ params, close }) => <Gift villager={params.villager} close={close} /> };
